// =============================================================
// useTaskCreate — posting a task and answering the near-duplicate gate, once.
//
// This was written out three times (`pages/Tasks.jsx`, `DealTasksPanel`,
// `Notes.jsx`), each carrying its own idempotency key, its own 409 branch and
// its own copy of the confirm dialog. Three copies of a rule is three chances to
// get it wrong, and TWO of them are invisible when they are:
//
//   - THE IDEMPOTENCY KEY IS HELD ACROSS RETRIES AND ROTATED ONLY ON SUCCESS.
//     Minting a fresh one per attempt turns a timeout the user retried into two
//     tasks; rotating too late turns the NEXT task into a silent no-op that
//     returns the previous one. Neither shows up in a happy path.
//   - A 409 IS NOT AN ERROR. It is the server saying "this looks like something
//     you already have" and it has to reach a human, with the resubmit carrying
//     the SAME key so confirming cannot create a second row.
//
// `runTaskCreate` keeps the exact contract the three call sites already had —
// it throws anything that is not a 409, so each caller's own catch still
// decides how to report it — and `duplicateDialog` is the rendered confirm, so
// nobody has to remember to wire one up.
// =============================================================

import { useState, useRef } from 'react';
import { api } from '../../api/client';
import { useToast } from '../ui';
import { DuplicateTaskDialog } from '../shared/DuplicateTaskDialog';

export const genRequestId = () =>
  (window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function useTaskCreate() {
  const { toast } = useToast();
  const [dupPrompt, setDupPrompt] = useState(null);
  const [dupBusy, setDupBusy]     = useState(false);

  // Stable across retries of ONE submission; rotated by the caller on success.
  const reqId = useRef(null);
  const requestId    = () => { if (!reqId.current) reqId.current = genRequestId(); return reqId.current; };
  const resetRequestId = () => { reqId.current = null; };

  // Post the create, routing a 409 "similar task" response into the confirm
  // dialog. `onSuccess` fires once the task is actually created — on the first
  // try, or after the user confirms (same payload + confirmDuplicate:true).
  const runTaskCreate = async (payload, onSuccess) => {
    try {
      const created = await api.post('tasks', payload);
      setDupPrompt(null);
      await onSuccess?.(created);
      return created;
    } catch (e) {
      if (e.status === 409 && e.body?.similarTasks?.length) {
        setDupPrompt({
          title: payload.title,
          similarTasks: e.body.similarTasks,
          resubmit: () => runTaskCreate({ ...payload, confirmDuplicate: true }, onSuccess),
        });
        return null;
      }
      throw e;
    }
  };

  const confirmDuplicate = async () => {
    if (!dupPrompt) return;
    setDupBusy(true);
    try { await dupPrompt.resubmit(); }
    catch (e) { toast.error(e.message); }
    finally { setDupBusy(false); }
  };

  const duplicateDialog = dupPrompt ? (
    <DuplicateTaskDialog
      newTitle={dupPrompt.title}
      similarTasks={dupPrompt.similarTasks}
      busy={dupBusy}
      onConfirm={confirmDuplicate}
      onCancel={() => setDupPrompt(null)}
    />
  ) : null;

  return { runTaskCreate, requestId, resetRequestId, duplicateDialog, dupPrompt, setDupPrompt };
}
